import { useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { PanHorizontalIcon } from '@dynatrace/strato-icons';

import { KeyboardShortcut } from '../../../../content/keyboard-shortcut/KeyboardShortcut.js';
import { KeyboardControls } from '../../../core/components/interactions-overlay/constants.js';
import { INTERACTION_MODE } from '../../../core/components/interactions-overlay/types.js';
import { reduceExploreCancelAll } from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { reduceInteractionMode } from '../../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { useSynchronizedSelection } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { TOOLBAR_MESSAGES } from '../../../core/components/toolbar/messages.js';
import { styledOrText } from '../../../core/components/toolbar/styles.sty.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import {
  useInteractionModeState,
  useSetState,
  useZoomState,
} from '../../hooks/store/selectors.js';
import { useChartInteractions } from '../../hooks/useChartInteractions.js';
import { TIMESERIES_MESSAGES } from '../../messages/tooltips-text.js';

export const PanButton = () => {
  const { zoomOutEnabled } = useZoomState();
  const panButtonRef = useRef<HTMLButtonElement>(null);
  const tabIndex = useA11yToolbarTabIndex(INTERACTION_MODE.Pan);
  const { resetSelectionState } = useSynchronizedSelection();
  const { pan } = useChartInteractions();
  const setState = useSetState();
  const intl = useIntl();

  const isPanEnabled = pan?.enabled;
  const interactionMode = useInteractionModeState();
  const isSelected = interactionMode === INTERACTION_MODE.Pan;

  const handleClick = () => {
    setState(reduceExploreCancelAll);
    resetSelectionState?.();
    setState(reduceInteractionMode(INTERACTION_MODE.Pan));
  };

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Enter') {
      handleClick();
    }
  };

  return isPanEnabled ? (
    <ChartToolbarRenderer.Control
      ref={panButtonRef}
      prefixIcon={<PanHorizontalIcon />}
      tooltipText={intl.formatMessage(TIMESERIES_MESSAGES.zoomModePan)}
      menuShortcut={
        <>
          <KeyboardShortcut
            keys="alt"
            variant="minimal"
            disabled={!zoomOutEnabled}
          />
          <span className={styledOrText}>
            {` ${intl.formatMessage(TOOLBAR_MESSAGES.or)} `}
          </span>
          <KeyboardShortcut
            keys={KeyboardControls.KEYBOARD_PAN_MODE_SHORTCUT_ALTERNATIVE}
            variant="minimal"
            disabled={!zoomOutEnabled}
          />
        </>
      }
      selected={isSelected}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      tabIndex={tabIndex}
      disabled={!zoomOutEnabled}
      placement="menu"
    />
  ) : null;
};

PanButton['displayName'] = 'PanButton';
